import { Menu, Transition } from "@headlessui/react";
import {
  BuildingOffice2Icon,
  ChartPieIcon,
  DocumentDuplicateIcon,
  HomeIcon,
  RectangleStackIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { useMemo } from "react";
import { Link, Outlet, useLocation, useMatches } from "react-router-dom";
import sidebarLogo from "../assets/sidebar-logo.svg";
import { useAuthentication } from "../hooks/useAuthentication.ts";
import cx from "../utils/cx.ts";

export default function AppLayout() {
  const matches = useMatches();
  const location = useLocation();
  const { user, signOut } = useAuthentication();

  const navigation = useMemo(() => {
    const routes = [
      {
        name: "Dashboard",
        href: "/",
        exactHref: true,
        icon: HomeIcon,
        current: true,
      },
      {
        name: "Sources",
        href: "/sources",
        icon: RectangleStackIcon,
        current: false,
      },
      {
        name: "Projects",
        href: "/projects",
        icon: BuildingOffice2Icon,
        current: false,
      },
      { name: "Enrollees", href: "/enrollees", icon: UsersIcon },
      {
        name: "Certificates",
        href: "/certificates",
        icon: DocumentDuplicateIcon,
        current: false,
      },
      { name: "Reports", href: "/reports", icon: ChartPieIcon },
    ];

    routes.reduceRight((found, route) => {
      if (found) {
        route.current = false;
        return found;
      }

      if (location.pathname.includes(route.href)) {
        route.current = true;
        return true;
      }

      return false;
    }, false);

    return routes;
  }, [matches]);

  return (
    <>
      <div className="flex h-full w-full">
        <div className="flex h-full w-[240px] flex-col bg-teal-600">
          <div className="h-[48px] flex items-center justify-center mt-5">
            <Link to={"/"}>
              <img
                className="w-[90px]"
                src={sidebarLogo}
                alt="Policy Management Tool"
              />
            </Link>
          </div>
          <div className="flex grow flex-col gap-y-5 overflow-y-auto px-6 mt-5">
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {navigation.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={cx(
                            item.current
                              ? "bg-teal-700 text-white"
                              : "text-teal-200",
                            "hover:text-white group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold",
                          )}
                        >
                          <item.icon
                            className={cx(
                              item.current ? "text-white" : "text-teal-200",
                              "h-6 w-6 shrink-0 group-hover:text-white",
                            )}
                            aria-hidden="true"
                          />
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
                {user != null && (
                  <li className="-mx-6 mt-auto">
                    {/* Profile dropdown */}
                    <Menu as="div" className="relative">
                      <div>
                        <Menu.Button className="w-full items-center justify-center flex max-w-xs text-md focus:outline-none hover:bg-teal-700 hover:border-teal-700">
                          <span className="sr-only">Open user menu</span>
                          <span aria-hidden="true">
                            {user?.attributes?.name}
                          </span>
                        </Menu.Button>
                      </div>
                      <Transition
                        enter="transition ease-out duration-200"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="absolute -top-[100px] left-[30px] z-10 mt-2 w-36 h-12 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                          <Menu.Item>
                            {({ active }) => (
                              <a
                                onClick={() => signOut()}
                                className={cx(
                                  active ? "bg-gray-100" : "",
                                  "block px-4 py-2 text-sm text-gray-700 cursor-pointer",
                                )}
                              >
                                Sign out
                              </a>
                            )}
                          </Menu.Item>
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  </li>
                )}
              </ul>
            </nav>
          </div>
        </div>
        <main className="bg-white h-full w-full overflow-x-auto">
          <Outlet />
        </main>
      </div>
    </>
  );
}
